import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Menu,
  Grid,
  Card,
  CardContent,
  Pagination,
  TableSortLabel,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  TableChart as TableChartIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Settings as SettingsIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const CSVContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  backgroundColor: 'white',
  borderRadius: '8px',
  margin: '20px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  [theme.breakpoints.down('sm')]: {
    margin: '12px',
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  '& .MuiTableHead-root': {
    backgroundColor: '#f8f9fa',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid rgba(87, 87, 176, 0.1)',
    color: '#1a202c',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  '& .MuiTableCell-body': {
    borderBottom: '1px solid rgba(87, 87, 176, 0.05)',
    padding: '12px 16px',
    fontSize: '0.9rem',
    fontFamily: 'monospace',
  },
  '& .MuiTableRow-root': {
    '&:nth-of-type(even)': {
      backgroundColor: '#f8f9fa',
    },
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.05)',
    },
  },
}));

const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 20px',
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  flexWrap: 'wrap',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  margin: '0 20px 20px 20px',
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: 'white',
  borderTop: '1px solid rgba(87, 87, 176, 0.1)',
}));

const ROWS_PER_PAGE = 100;

const CSVViewer = ({ open, onClose, filename = '', eventId = null, filePath = '' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: csvData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.csv_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [delimiter, setDelimiter] = useState(',');
  const [encoding, setEncoding] = useState('utf-8');
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSearchColumn('all');
      setSortColumn('');
      setSortDirection('asc');
      setCurrentPage(1);
    }
  }, [open, eventId, filePath]);

  const downloadCsv = () => {
    if (csvData && csvData.rows) {
      const headers = csvData.headers || [];
      const rows = csvData.rows || [];
      
      const csvContent = [
        headers.join(delimiter),
        ...rows.map(row => row.join(delimiter))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'data'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const downloadFilteredCsv = () => {
    if (csvData && csvData.rows) {
      const headers = csvData.headers || [];
      const filtered = getFilteredData();
      
      const csvContent = [
        headers.join(delimiter),
        ...filtered.map(row => row.join(delimiter))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'data'}_filtered.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Filter data based on search term and column
  const getFilteredData = () => {
    if (!csvData || !csvData.rows) return [];
    
    if (!searchTerm) return csvData.rows;
    
    const headers = csvData.headers || [];
    const searchLower = searchTerm.toLowerCase();
    
    return csvData.rows.filter(row => {
      if (searchColumn === 'all') {
        return row.some(cell => 
          cell && cell.toString().toLowerCase().includes(searchLower)
        );
      } else {
        const columnIndex = headers.indexOf(searchColumn);
        if (columnIndex !== -1 && row[columnIndex]) {
          return row[columnIndex].toString().toLowerCase().includes(searchLower);
        }
        return false;
      }
    });
  };

  // Sort data
  const getSortedData = (data) => {
    if (!sortColumn || !csvData || !csvData.headers) return data;
    
    const columnIndex = csvData.headers.indexOf(sortColumn);
    if (columnIndex === -1) return data;
    
    const sorted = [...data].sort((a, b) => {
      const aVal = a[columnIndex] || '';
      const bVal = b[columnIndex] || '';
      
      // Try to parse as numbers
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // String comparison
      const aStr = aVal.toString().toLowerCase();
      const bStr = bVal.toString().toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
    
    return sorted;
  };

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Get paginated data
  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const sorted = getSortedData(filtered);
    
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    
    return {
      data: sorted.slice(startIndex, endIndex),
      total: sorted.length,
      totalPages: Math.ceil(sorted.length / ROWS_PER_PAGE)
    };
  };

  const paginatedData = getPaginatedData();

  // Calculate statistics
  const getStatistics = () => {
    if (!csvData || !csvData.rows) return {};
    
    const filtered = getFilteredData();
    const headers = csvData.headers || [];
    
    return {
      totalRows: csvData.rows.length,
      filteredRows: filtered.length,
      columns: headers.length,
      nonEmptyRows: csvData.rows.filter(row => row.some(cell => cell && cell.toString().trim())).length,
    };
  };

  const stats = getStatistics();

  const handleSettingsClick = (event) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <TableChartIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.csv_viewer_title')} - ${filename}` : t('viewer.csv_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadCsv} 
            disabled={!csvData}
            size="small"
            title={t('viewer.download_csv')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={downloadFilteredCsv} 
            disabled={!csvData || paginatedData.total === 0}
            size="small"
            title="Download Filtered CSV"
          >
            <GetAppIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton 
            onClick={handleSettingsClick}
            size="small"
            title="Settings"
          >
            <SettingsIcon />
          </IconButton>
        </Box>
        
        {csvData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${stats.totalRows} rows`}
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`${stats.columns} columns`}
              size="small"
              variant="outlined"
            />
            {searchTerm && (
              <Chip 
                label={`${stats.filteredRows} filtered`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
        )}
      </ToolbarContainer>

      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleSettingsClose}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom>
            CSV Settings
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Delimiter</InputLabel>
            <Select
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
              label="Delimiter"
            >
              <MenuItem value=",">Comma (,)</MenuItem>
              <MenuItem value=";">Semicolon (;)</MenuItem>
              <MenuItem value="\t">Tab</MenuItem>
              <MenuItem value="|">Pipe (|)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Encoding</InputLabel>
            <Select
              value={encoding}
              onChange={(e) => setEncoding(e.target.value)}
              label="Encoding"
            >
              <MenuItem value="utf-8">UTF-8</MenuItem>
              <MenuItem value="iso-8859-1">ISO-8859-1</MenuItem>
              <MenuItem value="windows-1252">Windows-1252</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Menu>

      <DialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <CSVContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_csv')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_csv')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : csvData ? (
            <>
              {/* Statistics Card */}
              <StatsCard>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {stats.totalRows}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Rows
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {stats.columns}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Columns
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {stats.filteredRows}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Filtered Rows
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {Math.round((stats.nonEmptyRows / stats.totalRows) * 100)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Data Density
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StatsCard>

              {/* Filter Row */}
              <FilterRow>
                <TextField
                  size="small"
                  placeholder={t('viewer.search_csv_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Search Column</InputLabel>
                  <Select
                    value={searchColumn}
                    onChange={(e) => setSearchColumn(e.target.value)}
                    label="Search Column"
                  >
                    <MenuItem value="all">All Columns</MenuItem>
                    {csvData.headers && csvData.headers.map((header, index) => (
                      <MenuItem key={index} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  startIcon={<FilterListIcon />}
                  variant="outlined"
                  size="small"
                  onClick={clearSearch}
                  disabled={!searchTerm}
                >
                  Clear Filter
                </Button>
              </FilterRow>

              {/* Table */}
              <StyledTableContainer>
                <StyledTable stickyHeader>
                  <TableHead>
                    <TableRow>
                      {csvData.headers && csvData.headers.map((header, index) => (
                        <TableCell key={index}>
                          <TableSortLabel
                            active={sortColumn === header}
                            direction={sortColumn === header ? sortDirection : 'asc'}
                            onClick={() => handleSort(header)}
                          >
                            {header}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.data.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <TableCell key={cellIndex}>
                            {cell !== null && cell !== undefined ? cell.toString() : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </StyledTableContainer>

              {/* Pagination */}
              <PaginationContainer>
                <Pagination
                  count={paginatedData.totalPages}
                  page={currentPage}
                  onChange={(event, value) => setCurrentPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </PaginationContainer>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_csv_content')}
              </Typography>
            </Box>
          )}
        </CSVContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default CSVViewer;
